/**
 * Derive `{ currentPrice, salePrice }` (line total + per-unit) from a SCAPI
 * basket/order line item — the cart family's was/now derivation in ONE place,
 * shared by the cart/minicart line item (basket-line-item) and the order
 * confirmation summary so they can't drift (they each carried their own copy).
 *
 * `currentPrice` is the regular ("was") amount, `salePrice` the discounted
 * ("now") amount (≤ currentPrice). A sale is detected from an item-level
 * promotion (`priceAdjustments` present and `priceAfterItemDiscount` below the
 * line `price` — SFRA's `priceTotal.js` strikes `getPrice()` and shows
 * `adjustedPrice`), from `taxBasis` (a promotional basis below the line
 * `price`), or from `c_standardPrice * quantity` exceeding the line `price`.
 * Feed the result to `resolvePriceFromLineItem` (price-display.ts) →
 * `<ProductPrice>` for rendering.
 */
export interface LineItemPriceInput {
  price?: number;
  quantity?: number;
  taxBasis?: number;
  priceAdjustments?: Array<{ price?: number }>;
  priceAfterItemDiscount?: number;
  /** Custom attr — untyped on the SCAPI `ProductItem`, so narrowed at use. */
  c_standardPrice?: unknown;
}

export interface LineItemPrices {
  currentPrice?: number;
  salePrice?: number;
  unitCurrentPrice?: number;
  unitSalePrice?: number;
}

export function getLineItemPrices(
  lineItem: LineItemPriceInput,
): LineItemPrices {
  // `c_standardPrice` is a SCAPI custom attr that arrives as a numeric STRING
  // at runtime (the `number` type on ProductItem is optimistic) — coerce it, as
  // the original `(c_standardPrice as number) * quantity` did implicitly. A
  // `typeof === "number"` guard would silently zero it out and drop the sale.
  const standardUnit = Number(lineItem.c_standardPrice ?? 0);
  const standardPrice =
    (Number.isFinite(standardUnit) ? standardUnit : 0) *
    (lineItem.quantity ?? 0);

  let currentPrice = lineItem.price;
  // Item-level promotion (e.g. a % off products coupon) — takes precedence
  // over the taxBasis heuristic. `priceAfterItemDiscount` only reflects
  // item-level adjustments (order-level ones prorate into
  // `priceAfterOrderDiscount`), matching SFRA which never strikes line prices
  // for order-level promos. A 100%-off promo legitimately yields 0, so track
  // the branch explicitly rather than via salePrice truthiness.
  const hasItemPromoDiscount =
    (lineItem.priceAdjustments?.length ?? 0) > 0 &&
    lineItem.priceAfterItemDiscount != null &&
    lineItem.priceAfterItemDiscount < (lineItem.price ?? 0);
  let salePrice = hasItemPromoDiscount
    ? lineItem.priceAfterItemDiscount
    : lineItem.taxBasis && lineItem.taxBasis < (lineItem.price || 0)
      ? lineItem.taxBasis
      : undefined;

  if (
    !hasItemPromoDiscount &&
    !salePrice &&
    lineItem.price &&
    standardPrice &&
    lineItem.price < standardPrice
  ) {
    currentPrice = standardPrice;
  }

  if (
    !hasItemPromoDiscount &&
    !salePrice &&
    lineItem.price &&
    standardPrice &&
    standardPrice > lineItem.price
  ) {
    salePrice = lineItem.price;
  }

  const qty = lineItem.quantity || 1;
  const unitCurrentPrice =
    currentPrice != null ? currentPrice / qty : undefined;
  const unitSalePrice = salePrice != null ? salePrice / qty : undefined;

  return { salePrice, currentPrice, unitSalePrice, unitCurrentPrice };
}
